import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Tabs, Row, Col, Statistic, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';
import { FileTextOutlined, ShoppingOutlined, BarChartOutlined, DatabaseOutlined } from '@ant-design/icons';

const { Option } = Select;

interface PendienteFacturarItem {
  id: number;
  id_insumo: number;
  insumo_descripcion: string;
  cantidad_pendiente: number;
  padron: string;
  orden: string;
  fecha_emision: string;
}

const ListarPendienteFacturar: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<PendienteFacturarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroProveedor, setFiltroProveedor] = useState<string>('todos');
  const [filtroInsumo, setFiltroInsumo] = useState<string>('todos');

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;


  const columns: ColumnsType<PendienteFacturarItem> = [
    {
      title: 'Proveedor',
      dataIndex: 'padron',
      key: 'padron',
      sorter: (a, b) => a.padron.localeCompare(b.padron),
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.padron))).map(padron => ({
        text: padron,
        value: padron,
      })),
      onFilter: (value, record) => record.padron === value,
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo_descripcion',
      key: 'insumo_descripcion',
      sorter: (a, b) => a.insumo_descripcion.localeCompare(b.insumo_descripcion),
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.insumo_descripcion))).map(insumo => ({
        text: insumo,
        value: insumo,
      })),
      onFilter: (value, record) => record.insumo_descripcion === value,
    },
    {
      title: 'Cantidad a Facturar',
      dataIndex: 'cantidad_pendiente',
      key: 'cantidad_pendiente',
      sorter: (a, b) => a.cantidad_pendiente - b.cantidad_pendiente,
    },
  ];

  const fetchData = async () => {
    if (!empresa || !sucursal) {
      message.error('Faltan datos necesarios para la consulta');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/pendiente_facturar`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos');
      }
      console.log('response', response);
      const responseData = await response.json() as PendienteFacturarItem[];
      setData(responseData);
    } catch (error) {
      message.error('Error al cargar los datos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa && sucursal) {
      fetchData();
    }
  }, [empresa, sucursal]);

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    // Filtrar datos según filtros seleccionados
    let datosFiltrados = data;
    if (filtroProveedor !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.padron === filtroProveedor);
    }
    if (filtroInsumo !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.insumo_descripcion === filtroInsumo);
    }

    const totalRegistros = datosFiltrados.length;
    const totalCantidadPendiente = datosFiltrados.reduce((sum, item) => sum + (item.cantidad_pendiente || 0), 0);
    const promedioPorRegistro = totalRegistros > 0 ? totalCantidadPendiente / totalRegistros : 0;
    
    // Distribución por proveedor
    const porProveedor = datosFiltrados.reduce((acc, item) => {
      const proveedor = item.padron || 'Sin proveedor';
      if (!acc[proveedor]) {
        acc[proveedor] = { cantidad: 0, totalPendiente: 0 };
      }
      acc[proveedor].cantidad += 1;
      acc[proveedor].totalPendiente += item.cantidad_pendiente || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalPendiente: number }>);
    
    const distribucionProveedor = Object.entries(porProveedor)
      .map(([proveedor, datos]) => ({
        proveedor,
        cantidad: datos.cantidad,
        totalPendiente: datos.totalPendiente,
        porcentaje: totalCantidadPendiente > 0 ? (datos.totalPendiente / totalCantidadPendiente) * 100 : 0
      }))
      .sort((a, b) => b.totalPendiente - a.totalPendiente)
      .slice(0, 10);
    
    // Distribución por insumo
    const porInsumo = datosFiltrados.reduce((acc, item) => {
      const insumo = item.insumo_descripcion || 'Sin insumo';
      if (!acc[insumo]) {
        acc[insumo] = { cantidad: 0, totalPendiente: 0 };
      }
      acc[insumo].cantidad += 1;
      acc[insumo].totalPendiente += item.cantidad_pendiente || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalPendiente: number }>);
    
    const distribucionInsumo = Object.entries(porInsumo)
      .map(([insumo, datos]) => ({
        insumo,
        cantidad: datos.cantidad,
        totalPendiente: datos.totalPendiente,
        porcentaje: totalCantidadPendiente > 0 ? (datos.totalPendiente / totalCantidadPendiente) * 100 : 0
      }))
      .sort((a, b) => b.totalPendiente - a.totalPendiente)
      .slice(0, 10);
    
    // Distribución por orden
    const porOrden = datosFiltrados.reduce((acc, item) => {
      const orden = item.orden || 'Sin orden';
      if (!acc[orden]) {
        acc[orden] = { cantidad: 0, totalPendiente: 0 };
      }
      acc[orden].cantidad += 1;
      acc[orden].totalPendiente += item.cantidad_pendiente || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalPendiente: number }>);
    
    const distribucionOrden = Object.entries(porOrden)
      .map(([orden, datos]) => ({
        orden,
        cantidad: datos.cantidad,
        totalPendiente: datos.totalPendiente,
        porcentaje: totalCantidadPendiente > 0 ? (datos.totalPendiente / totalCantidadPendiente) * 100 : 0
      }))
      .sort((a, b) => b.totalPendiente - a.totalPendiente)
      .slice(0, 10);
    
    const proveedoresUnicos = Array.from(new Set(data.map(item => item.padron))).sort();
    const insumosUnicos = Array.from(new Set(data.map(item => item.insumo_descripcion))).sort();
    
    return {
      totalRegistros,
      totalCantidadPendiente,
      promedioPorRegistro,
      distribucionProveedor,
      distribucionInsumo,
      distribucionOrden,
      proveedoresUnicos,
      insumosUnicos
    };
  }, [data, filtroProveedor, filtroInsumo]);

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Pendientes de Facturación
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Informe de Insumos Pendientes de Facturar"
      >
        <Tabs
          defaultActiveKey="listado"
          size="small"
          items={[
            {
              key: 'listado',
              label: 'Listado',
              children: (
                <Table
                  columns={columns}
                  dataSource={data}
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} registros`,
                  }}
                />
              )
            },
            {
              key: 'estadisticas',
              label: 'Estadísticas',
              children: (
                <div style={{ padding: '20px 0' }}>
                  {/* Filtros */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Proveedor:</div>
                      <Select
                        value={filtroProveedor}
                        onChange={setFiltroProveedor}
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Todos los proveedores"
                      >
                        <Option value="todos">Todos</Option>
                        {estadisticas.proveedoresUnicos.map(prov => (
                          <Option key={prov} value={prov}>{prov}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Insumo:</div>
                      <Select
                        value={filtroInsumo}
                        onChange={setFiltroInsumo}
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Todos los insumos"
                      >
                        <Option value="todos">Todos</Option>
                        {estadisticas.insumosUnicos.map(ins => (
                          <Option key={ins} value={ins}>{ins}</Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>

                  {/* Tarjetas de resumen */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total de Registros"
                          value={estadisticas.totalRegistros}
                          prefix={<FileTextOutlined />}
                          valueStyle={{ color: theme.colors.primary }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total Cantidad Pendiente"
                          value={estadisticas.totalCantidadPendiente}
                          prefix={<ShoppingOutlined />}
                          valueStyle={{ color: theme.colors.success }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Promedio por Registro"
                          value={estadisticas.promedioPorRegistro.toFixed(2)}
                          prefix={<BarChartOutlined />}
                          valueStyle={{ color: theme.colors.info }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Proveedores Únicos"
                          value={estadisticas.proveedoresUnicos.length}
                          prefix={<DatabaseOutlined />}
                          valueStyle={{ color: theme.colors.warning }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* Distribución por Proveedor */}
                  <Card 
                    title="Top 10 - Distribución por Proveedor" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionProveedor}
                      rowKey="proveedor"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Proveedor',
                          dataIndex: 'proveedor',
                          key: 'proveedor',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Pendiente',
                          dataIndex: 'totalPendiente',
                          key: 'totalPendiente',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>

                  {/* Distribución por Insumo */}
                  <Card 
                    title="Top 10 - Distribución por Insumo" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionInsumo}
                      rowKey="insumo"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Insumo',
                          dataIndex: 'insumo',
                          key: 'insumo',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Pendiente',
                          dataIndex: 'totalPendiente',
                          key: 'totalPendiente',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>

                  {/* Distribución por Orden */}
                  <Card 
                    title="Top 10 - Distribución por Orden" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionOrden}
                      rowKey="orden"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Orden',
                          dataIndex: 'orden',
                          key: 'orden',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Pendiente',
                          dataIndex: 'totalPendiente',
                          key: 'totalPendiente',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default ListarPendienteFacturar;